import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { serializeValidationConfig } from '@alchemy/smart-accounts';
import {
  encodeFunctionData,
  parseAbi,
  toFunctionSelector,
  type Address,
} from 'viem';
import { z } from 'zod';
import { experimentalExternalCoexistenceModuleId } from './externalFixedRouteOrderAbi.js';

const EXECUTION_HOOK_ENTITY_ID = 1;
const RUNTIME_VALIDATION_ENTITY_ID = 2;

const nonzeroAddress = z.string()
  .regex(/^0x[0-9a-fA-F]{40}$/)
  .refine(value => !/^0x0{40}$/i.test(value))
  .transform(value => value.toLowerCase() as Address);

const inputSchema = z.strictObject({
  version: z.literal('external-fixed-route-order.1'),
  moduleVersion: z.literal(experimentalExternalCoexistenceModuleId),
  chainId: z.literal(4663),
  vault: nonzeroAddress,
  engine: nonzeroAddress,
});

const accountAbi = parseAbi([
  'function installExecution(address module,((bytes4 executionSelector,bool skipRuntimeValidation,bool allowGlobalValidation)[] executionFunctions,(bytes4 executionSelector,uint32 entityId,bool isPreHook,bool isPostHook)[] executionHooks,bytes4[] interfaceIds) manifest,bytes data)',
  'function installValidation(bytes25 config,bytes4[] selectors,bytes data,bytes[] hooks)',
  'function execute(address target,uint256 value,bytes data) payable returns(bytes)',
]);

const engineAbi = parseAbi(['function armInitialSetup()']);
const executeBatchSelector = toFunctionSelector('executeBatch((address,uint256,bytes)[])');
const authoritySignatures = [
  'updateFallbackSignerData(address,bool)',
  'installValidation(bytes25,bytes4[],bytes,bytes[])',
  'uninstallValidation(bytes24,bytes,bytes[])',
  'installExecution(address,((bytes4,bool,bool)[],(bytes4,uint32,bool,bool)[],bytes4[]),bytes)',
  'uninstallExecution(address,((bytes4,bool,bool)[],(bytes4,uint32,bool,bool)[],bytes4[]),bytes)',
  'upgradeToAndCall(address,bytes)',
] as const;

export type ExternalExecutorSetupInput<ChainId extends 4663 | 46630 = 4663> =
  Omit<z.input<typeof inputSchema>, 'chainId'> & { chainId: ChainId };
export type SelectedExternalExecutorSetupInput = ExternalExecutorSetupInput<typeof SELECTED_ACCOUNT_CHAIN_ID>;

function executorSetupPlanner<const ChainId extends 4663 | 46630>(chainId: ChainId) {
  const domainSchema = inputSchema.extend({ chainId: z.literal(chainId) });
  /**
   * Builds the three exact, separate owner operations for a fresh experimental
   * external fixed-route executor setup. It performs no reads or effects.
   *
   * The final call deliberately uses `armInitialSetup()`, whose engine-side
   * contract requires the fresh epoch/order state. Preserved local replays that
   * call `arm()` exercise an already-staged topology and are not this planner's
   * authority model. Before the first step, the caller must independently prove
   * current owner, vault deployment/provenance, engine/runtime pins, compatible
   * current topology and initial installation absence/history. Each later step
   * requires the prior step's exact receipt plus a fresh canonical topology read,
   * while owner and runtime pins must remain current throughout. Route
   * configuration is a later, separate owner operation and is never included.
   */
  function prepareExternalExecutorSetup(input: ExternalExecutorSetupInput<ChainId>) {
    const record = domainSchema.parse(structuredClone(input));
    if (record.vault === record.engine) throw new Error('External executor must differ from vault');

    const executionHooks = authoritySignatures.map(executionSelector => ({
      executionSelector: toFunctionSelector(executionSelector),
      entityId: EXECUTION_HOOK_ENTITY_ID,
      isPreHook: true,
      isPostHook: false,
    }));
    const installExecution = {
      to: record.vault,
      value: 0n,
      data: encodeFunctionData({
        abi: accountAbi,
        functionName: 'installExecution',
        args: [record.engine, { executionFunctions: [], executionHooks, interfaceIds: [] }, '0x01'],
      }),
    };

    const validationConfig = serializeValidationConfig({
      moduleAddress: record.engine,
      entityId: RUNTIME_VALIDATION_ENTITY_ID,
      isGlobal: false,
      isUserOpValidation: false,
      isSignatureValidation: false,
    });
    const installValidation = {
      to: record.vault,
      value: 0n,
      data: encodeFunctionData({
        abi: accountAbi,
        functionName: 'installValidation',
        args: [validationConfig, [executeBatchSelector], '0x01', []],
      }),
    };

    const armInitialSetup = {
      to: record.vault,
      value: 0n,
      data: encodeFunctionData({
        abi: accountAbi,
        functionName: 'execute',
        args: [record.engine, 0n, encodeFunctionData({ abi: engineAbi, functionName: 'armInitialSetup' })],
      }),
    };

    return {
      version: record.version,
      moduleVersion: record.moduleVersion,
      chainId: record.chainId,
      vault: record.vault,
      engine: record.engine,
      steps: [
        { kind: 'install-execution-hooks' as const, call: installExecution },
        { kind: 'install-runtime-validation' as const, call: installValidation },
        { kind: 'arm-initial-setup' as const, call: armInitialSetup },
      ] as const,
      freshInitialSetupOnly: true as const,
      routeConfigured: false as const,
      currentOwnerVerified: false as const,
      deploymentVerified: false as const,
      modulesVerified: false as const,
      profileApproved: false as const,
      setupAuthorized: false as const,
      signingAuthorized: false as const,
      submissionAuthorized: false as const,
      executionAuthorized: false as const,
      atomic: false as const,
    };
  }
  return prepareExternalExecutorSetup;
}

export const prepareExternalExecutorSetup: ReturnType<typeof executorSetupPlanner<4663>> = executorSetupPlanner(4663);
export const prepareSelectedExternalExecutorSetup: ReturnType<typeof executorSetupPlanner<typeof SELECTED_ACCOUNT_CHAIN_ID>> = executorSetupPlanner(SELECTED_ACCOUNT_CHAIN_ID);
export type ExternalExecutorSetupPlan<ChainId extends 4663 | 46630 = 4663> = ReturnType<ReturnType<typeof executorSetupPlanner<ChainId>>>;
export type SelectedExternalExecutorSetupPlan = ExternalExecutorSetupPlan<typeof SELECTED_ACCOUNT_CHAIN_ID>;
